// Routes

"use strict";

import { createRouter, createWebHistory, Router, RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from "vue-router";
import { Timeouts } from "./utils/timeout";
import { AppEvents } from "./control/app-events";
import { AuthController } from "./control/auth";
import { isPublicAuthRoute } from "./navigation-constants";
import { clone } from "./utils/objects";

// Router
// https://router.vuejs.org/guide/#javascript

// List of forbidden routes:
//
//    - /api/*  - This is reserved for the API
//    - /static/* - This is reserved for static assets
//    - /webhooks*/ - Reserved for webhooks

const routes: (RouteRecordRaw & {
    meta?: {
        /**
         * Set it to true for sticky sidebar
         */
        sidebarSticky?: boolean;
    };
})[] = [
    /* General / Home */

    {
        name: "home",
        path: "/",
        component: () => import("@/components/routes/projects/ProjectsPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "project-detail",
        path: "/projects/:id",
        component: () => import("@/components/routes/projects/ProjectDetailPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "servers",
        path: "/servers",
        component: () => import("@/components/routes/servers/ServersPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "server-alerts",
        path: "/server-alerts",
        component: () => import("@/components/routes/servers/ServerAlertsPage.vue"),
        meta: { sidebarSticky: true },
    },

    {
        name: "server-detail",
        path: "/servers/:id",
        component: () => import("@/components/routes/servers/ServerDetailPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "check-detail",
        path: "/checks/:id",
        component: () => import("@/components/routes/projects/CheckDetailPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "about",
        path: "/about",
        component: () => import("@/components/routes/AboutPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "terms",
        path: "/terms",
        component: () => import("@/components/routes/TermsOfUsePage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "cookies",
        path: "/cookies",
        component: () => import("@/components/routes/CookiePolicyPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    {
        name: "privacy",
        path: "/privacy",
        component: () => import("@/components/routes/PrivacyPolicyPage.vue"),
        meta: {
            sidebarSticky: true,
        },
    },

    /* Auth (public without an account; every other view requires login) */

    {
        name: "login",
        path: "/login",
        component: () => import("@/components/routes/auth/LoginPage.vue"),
    },

    {
        name: "forgot-password",
        path: "/password/forgot",
        component: () => import("@/components/routes/auth/ForgotPasswordPage.vue"),
    },
    {
        name: "reset-password",
        path: "/password/reset/:uid/:token",
        component: () => import("@/components/routes/auth/ResetPasswordPage.vue"),
    },

    {
        name: "verify-email",
        path: "/email/verify/:uid/:token",
        component: () => import("@/components/routes/auth/EmailVerifyPage.vue"),
    },

    /* Profile */

    {
        name: "profile",
        path: "/user/:username",
        component: () => import("@/components/routes/profile/ProfilePage.vue"),
    },

    /* Account */

    {
        name: "account-settings",
        path: "/account-settings",
        component: () => import("@/components/routes/account/AccountSettingsPage.vue"),
    },

    /* Admin */

    {
        name: "admin",
        path: "/admin",
        component: () => import("@/components/routes/admin/AdministrationPage.vue"),
    },

    {
        name: "admin-user",
        path: "/admin/users/:id",
        component: () => import("@/components/routes/admin/UserPage.vue"),
    },

    /* Default */

    {
        path: "/:catchAll(.*)",
        component: () => import("@/components/routes/NotFoundPage.vue"),
    },
];

function redirectToLoginIfUnauthenticated(route: RouteLocationNormalized): RouteLocationRaw | true {
    if (AuthController.isAuthenticated() || isPublicAuthRoute(route.name)) {
        return true;
    }

    if (typeof route.name === "string") {
        AuthController.PageToGo = {
            name: route.name,
            params: clone(route.params),
            query: clone(route.query),
        };
    }

    return { name: "login", replace: true };
}

export function makeApplicationRouter(): Router {
    const router = createRouter({
        // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
        history: createWebHistory(),
        routes, // short for `routes: routes`
    });

    router.beforeEach(async (to) => {
        await AuthController.WaitUntilReady();
        return redirectToLoginIfUnauthenticated(to);
    });

    AppEvents.AddEventListener("auth-status-changed", () => {
        if (AuthController.Loading) {
            return;
        }

        const current = router.currentRoute.value;
        if (!current.matched.length) {
            return;
        }

        const redirect = redirectToLoginIfUnauthenticated(current);
        if (redirect !== true) {
            router.replace(redirect);
        }
    });

    router.beforeEach(() => {
        Timeouts.Set("router-load-state", 300, () => {
            AppEvents.Emit("router-load-state-change", true);
        });
    });

    router.afterEach(() => {
        Timeouts.Abort("router-load-state");
        AppEvents.Emit("router-load-state-change", false);
    });

    return router;
}
